import { ArtefactCitationFeedbackDrawerContent } from '@modules/oracle/components/ArtefactCitationFeedbackDrawer/ArtefactCitationFeedbackDrawerContent';
import { Drawer } from '@mui/material';
import { captureMessage } from '@sentry/react';
import { AssistantResponseArtefactToken } from 'generated/graphql';
import { useMemo } from 'react';
import useGetSourceDetailsMap from 'v2/core/repository/edge-server/source-details';
import { parseEQL } from 'v2/core/repository/eql/parser';
import { useGetUser } from 'v2/core/services/user';

interface ArtefactCitationFeedbackDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  artefact:
    | Pick<
        AssistantResponseArtefactToken,
        'analysis' | 'feedbackCount' | 'forClaim'
      >
    | undefined;
}

export const ArtefactCitationFeedbackDrawer = ({
  isOpen,
  onClose,
  artefact,
}: ArtefactCitationFeedbackDrawerProps) => {
  const { data: sourceDetailsMap } = useGetSourceDetailsMap();

  const {
    tenant: { timezone },
  } = useGetUser();

  const feedbackRecordID = useMemo(() => {
    const eql = artefact?.analysis?.context.eqlExpression;

    if (!eql || !sourceDetailsMap) {
      return null;
    }

    try {
      const parsedWhereClause = parseEQL(
        eql,
        sourceDetailsMap,
        timezone,
      ).whereClause;

      const idValue = parsedWhereClause?.filter?.idFilter?.ids[0];

      // In audio records, the ID string is appended with other info, so we need to extract the record ID
      // Format: recordUUID[Int:Int][labelID=UUID][summaryHash=UUID][summaryIdx=Int]
      const recordID = idValue?.split('[')[0];
      return recordID ?? null;
    } catch {
      captureMessage('Error while parsing EQL in Wisdom citation', {
        extra: {
          eql: artefact?.analysis?.context.eqlExpression,
        },
        level: 'error',
      });
      return null;
    }
  }, [artefact?.analysis?.context.eqlExpression, sourceDetailsMap, timezone]);

  return (
    <Drawer
      anchor="right"
      open={isOpen}
      onClose={onClose}
      keepMounted={false}
      PaperProps={{
        sx: {
          width: 'calc(100vw - 330px)',
          overflow: 'hidden',
        },
      }}
    >
      <ArtefactCitationFeedbackDrawerContent recordID={feedbackRecordID} />
    </Drawer>
  );
};
